import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '@/components/PageHero';
import FadeInUp from '@/components/FadeInUp';
import { COURSES, ADDITIONAL_SERVICES } from '@/lib/site';
import { ForkliftIcon, RocketIcon, ReachIcon, BadgeIcon, LicenseIcon, CertificateIcon, ChevronRight, ArrowRight, ShieldIcon, ClockIcon } from '@/components/icons';

const ICONS = { forklift: ForkliftIcon, rocket: RocketIcon, reach: ReachIcon, badge: BadgeIcon, license: LicenseIcon, certificate: CertificateIcon };

export default function Courses() {
  return (
    <>
      <PageHero
        eyebrow="Courses & Services"
        title="Six course streams — plus consulting, audits & workshops."
        subtitle="NFT has designed and customised its forklift courses to suit a variety of industries and individuals. Courses are conducted to suit the trainees’ and site-specific needs."
        image="/generated/hero-warehouse.png"
        breadcrumbs={[{label: 'Home', to: '/'}, {label: 'Courses'}]}
      />

      <section className="nft-container py-16 lg:py-24">
        <FadeInUp>
          <div className="label-eyebrow text-[#FC4E2C] mb-3">Choose Your Stream</div>
          <h2 className="font-display font-extrabold uppercase text-4xl lg:text-5xl text-[#060D9C] leading-[0.95]">
            Forklift courses to fit every operation.
          </h2>
          <span className="section-rule mt-5" />
        </FadeInUp>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
          {COURSES.map((c, i) => {
            const Icon = ICONS[c.icon] || ForkliftIcon;
            return (
              <FadeInUp key={i} delay={i * 0.05}>
                <Link
                  to={`/courses/${c.slug}`}
                  className="surface-card overflow-hidden block group h-full flex flex-col"
                  data-testid="courses-card"
                >
                  <div className="aspect-[16/10] relative bg-[#060D9C] overflow-hidden">
                    <img src={c.image} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#060D9C]/90 via-[#060D9C]/30 to-transparent" />
                    <div className="absolute top-3 left-3 w-10 h-10 rounded-lg bg-[#FC4E2C] text-white flex items-center justify-center">
                      <Icon width={22} height={22} />
                    </div>
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white text-xs">
                      <span className="label-eyebrow flex items-center gap-1.5"><ClockIcon width={14} height={14} />{c.duration}</span>
                      <span className="label-eyebrow opacity-80">{c.audience}</span>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-display font-extrabold uppercase text-2xl text-[#060D9C] leading-tight">{c.title}</h3>
                    <p className="mt-3 text-[#060D9C]/75 text-sm leading-relaxed flex-1">{c.short}</p>
                    <div className="mt-5 inline-flex items-center gap-1 text-[#FC4E2C] text-sm font-bold uppercase" style={{ fontFamily: 'Barlow Semi Condensed', letterSpacing: '0.08em' }}>
                      Learn more <ChevronRight width={14} height={14} />
                    </div>
                  </div>
                </Link>
              </FadeInUp>
            );
          })}
        </div>
      </section>

      {/* WORKSHOPS / CONSULTING */}
      <section className="bg-[#060D9C] text-white">
        <div className="nft-container py-16 lg:py-24">
          <FadeInUp>
            <div className="label-eyebrow text-[#FC4E2C] mb-3">Beyond Courses</div>
            <h2 className="font-display font-extrabold uppercase text-4xl lg:text-5xl leading-[0.95]">
              Workshops, audits, <span className="text-[#FC4E2C]">consulting.</span>
            </h2>
            <p className="mt-4 text-white/80 max-w-2xl">
              Beyond operator certification, NFT supports your operation end-to-end — from layout planning to incident investigation.
            </p>
          </FadeInUp>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {ADDITIONAL_SERVICES.map((s, i) => (
              <FadeInUp key={i} delay={i * 0.03}>
                <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#FC4E2C]/40 transition-colors">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                    <ShieldIcon width={20} height={20} />
                  </div>
                  <span className="font-bold text-sm uppercase" style={{ fontFamily: 'Barlow Semi Condensed', letterSpacing: '0.05em' }}>{s}</span>
                </div>
              </FadeInUp>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/booking" className="btn-primary-orange">
              Enquire about a workshop <ArrowRight width={18} height={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
